import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Breadcrumb from "../../components/Breadcrumb";
import RightArrow from "../../components/SVG";
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";
import { Modal, Button } from "react-bootstrap";
import toast from "react-hot-toast";
import "bootstrap/dist/css/bootstrap.min.css";
import { useAuth } from "../../context/AdminAuthContext";
import SingleEvent from "../../components/Event";

const EventDetailsMain = ({
  event,
  relatedEvents = [], // Add relatedEvents prop
  isLoading,
  isError,
  error,
  formattedTitle,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const { auth } = useAuth();
  const navigate = useNavigate();

  const galleryImages = event?.galleryImages || [];
  const presentations = event?.presentations || [];
  const resources = event?.resources || []; // Added resources for videos

  // Check if user is authenticated
  const isAuthenticated = !!auth?.token;

  function openPopus(index) {
    setSelectedIndex(index);
    setIsOpen(true);
  }

  const toggleModal = () => {
    setIsOpen(!isOpen);
  };

  const nextImage = () => {
    setSelectedIndex((nextIndex) => (nextIndex + 1) % galleryImages.length);
  };

  const prevImage = () => {
    setSelectedIndex((prevIndex) =>
      galleryImages.length > 0
        ? (prevIndex - 1 + galleryImages.length) % galleryImages.length
        : 0,
    );
  };

  // Helper function to convert any YouTube link into an iframe embed link
  const getYouTubeEmbedUrl = (url) => {
    if (!url) return "";
    let videoId = "";

    try {
      if (url.includes("shorts/")) {
        videoId = url.split("shorts/")[1].split("?")[0];
      } else if (url.includes("watch?v=")) {
        videoId = url.split("watch?v=")[1].split("&")[0];
      } else if (url.includes("youtu.be/")) {
        videoId = url.split("youtu.be/")[1].split("?")[0];
      }
    } catch (e) {
      console.error("Error parsing YouTube URL:", e);
    }

    return videoId ? `https://www.youtube.com/embed/${videoId}` : url;
  };

  // Handle download action with authentication check
  const handleDownload = (url, title) => {
    if (isAuthenticated) {
      try {
        const link = document.createElement("a");
        link.href = url;
        link.download = title || "download";
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        toast.success("Download started successfully!");
      } catch (error) {
        toast.error("Download failed. Please try again.");
        console.error("Download error:", error);
      }
    } else {
      navigate("/login");
    }
  };

  // Handle event navigation
  const handleEventClick = (eventId, eventTitle) => {
    const formattedTitle = eventTitle.replace(/\s+/g, "+");
    navigate(`/event-details?eventId=${eventId}&title=${formattedTitle}`);
  };

  // Format event description for preview
  const formatDescription = (description, maxLength = 120) => {
    if (!description) return "No description available.";
    const strippedDescription = description.replace(/<[^>]+>/g, "");
    return strippedDescription.length > maxLength
      ? strippedDescription.substring(0, maxLength) + "..."
      : strippedDescription;
  };

  if (isLoading) return <p className="text-center py-5">Loading event...</p>;
  if (isError)
    return <p className="text-danger text-center py-5">{error.message}</p>;

  if (!event) {
    return <p className="text-center py-5">No event details available.</p>;
  }

  return (
    <main>
      <Breadcrumb
        title={formattedTitle}
        subtitle={{ label: "Event", link: "/event" }}
      />

      <div className="postbox__area fix pt-50 pb-0">
        <div className="container">
          <div className="row">
            <div className="col-xxl-12 mb-50 order-3 order-md-0">
              <div className="it-blog-button text-start text-md-end">
                <Link className="ed-btn-theme" to="/event">
                  Back to Event Page
                  <i>
                    <RightArrow />
                  </i>
                </Link>
              </div>
            </div>

            <div className="col-xxl-12 min-h-screen">
              <div className="postbox__details-wrapper">
                <article>
                  <div className="postbox__details-title-box pb-0">
                    <div className="postbox__meta">
                      <span>
                        <i className="fa-solid fa-calendar-days"></i>{" "}
                        {event?.date
                          ? new Date(event.date).toLocaleDateString("en-IN", {
                              day: "numeric",
                              month: "long",
                              year: "numeric",
                            })
                          : "Date not available"}
                      </span>
                    </div>
                    <h4 className="postbox__title mb-20 pr-30">
                      {event?.title || "No title available"}
                    </h4>
                  </div>

                  {/* Tabs Section */}
                  <Tabs className="it-course-details-wrap mr-0">
                    <div className="it-course-details-nav pb-0 pb-md-5">
                      <nav>
                        <TabList
                          className="nav nav-tab"
                          id="nav-tab"
                          role="tablist"
                        >
                          <Tab>
                            <button>About Event</button>
                          </Tab>

                          {presentations.length > 0 && (
                            <Tab>
                              <button>
                                Event Presentation
                                {!isAuthenticated && (
                                  <i
                                    className="fa-solid fa-lock ms-2"
                                    title="Login required"
                                  ></i>
                                )}
                              </button>
                            </Tab>
                          )}

                          {galleryImages.length > 0 && (
                            <Tab>
                              <button>Event Gallery</button>
                            </Tab>
                          )}

                          {/* New Tab for Event Videos */}
                          {resources.length > 0 && (
                            <Tab>
                              <button>Event Videos</button>
                            </Tab>
                          )}
                        </TabList>
                      </nav>
                    </div>

                    <div className="it-course-details-content">
                      <div className="tab-content" id="nav-tabContent">
                        {/* About Event Tab */}
                        <TabPanel>
                          <div className="row">
                            <div className="col-xxl-8">
                              <div className="postbox__details-wrapper">
                                <article>
                                  {event?.description ? (
                                    <div
                                      className="event-description-content mb-0 pb-10"
                                      dangerouslySetInnerHTML={{
                                        __html: event.description,
                                      }}
                                    />
                                  ) : (
                                    <p>
                                      No description available for this event.
                                    </p>
                                  )}
                                </article>
                              </div>
                            </div>
                            <div className="col-xxl-4 col-xl-4 col-lg-4">
                              <div className="event-main-image-wrapper">
                                {event?.mainImage && (
                                  <div className="event-main-image">
                                    <img
                                      src={event.mainImage}
                                      alt={event.title || "Event main image"}
                                      style={{
                                        width: "100%",
                                        height: "auto",
                                        borderRadius: "8px",
                                        boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                                        objectFit: "cover",
                                      }}
                                      className="img-fluid"
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </TabPanel>

                        {/* Presentations Tab */}
                        {presentations.length > 0 && (
                          <TabPanel>
                            <div className="row mb-50 justify-content-center">
                              <div className="col-xxl-10">
                                <div className="table-content table-responsive">
                                  <table className="table">
                                    <thead>
                                      <tr>
                                        <th className="text-start">Title</th>
                                        <th className="text-center">
                                          Get the Article
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {presentations.map(
                                        (presentation, idx) => (
                                          <tr key={idx}>
                                            <td className="text-start">
                                              <div className="d-flex align-items-center">
                                                <i className="fa-solid fa-file-pdf me-2 text-danger"></i>
                                                <span>
                                                  {presentation.title}
                                                </span>
                                                {!isAuthenticated && (
                                                  <i
                                                    className="fa-solid fa-lock ms-2 text-muted"
                                                    title="Login required"
                                                  ></i>
                                                )}
                                              </div>
                                            </td>
                                            <td className="text-center">
                                              <div className="">
                                                {isAuthenticated ? (
                                                  <button
                                                    onClick={() =>
                                                      handleDownload(
                                                        presentation.url,
                                                        presentation.title,
                                                      )
                                                    }
                                                    className="btn w-[200px] ed-btn-theme text-white"
                                                    style={{
                                                      paddingLeft: "20px",
                                                      paddingRight: "20px",
                                                      borderRadius: "5px",
                                                      border: "none",
                                                      cursor: "pointer",
                                                    }}
                                                  >
                                                    Download
                                                  </button>
                                                ) : (
                                                  <button
                                                    onClick={() =>
                                                      handleDownload(
                                                        presentation.url,
                                                        presentation.title,
                                                      )
                                                    }
                                                    className="btn btn-outline-primary"
                                                    style={{
                                                      paddingLeft: "15px",
                                                      paddingRight: "15px",
                                                      borderRadius: "5px",
                                                      cursor: "pointer",
                                                    }}
                                                  >
                                                    <i className="fa-solid fa-sign-in-alt me-2"></i>
                                                    Login and Download
                                                  </button>
                                                )}
                                              </div>
                                              {!isAuthenticated && (
                                                <div className="mt-2">
                                                  <small className="text-muted">
                                                    <i className="fa-solid fa-info-circle me-1"></i>
                                                    Only members can download
                                                  </small>
                                                </div>
                                              )}
                                            </td>
                                          </tr>
                                        ),
                                      )}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </div>
                          </TabPanel>
                        )}

                        {/* Gallery Tab */}
                        {galleryImages.length > 0 && (
                          <TabPanel>
                            <div className="row mb-50">
                              {galleryImages.map((image, index) => (
                                <div
                                  className="col-xxl-3 col-xl-3 col-lg-3"
                                  key={index}
                                >
                                  <div className="it-evn-details-thumb mb-35">
                                    <img
                                      src={image}
                                      alt={`Event image ${index + 1}`}
                                      style={{
                                        width: "100%",
                                        height: "240px",
                                        objectFit: "cover",
                                        borderRadius: "5px",
                                        cursor: "pointer",
                                      }}
                                      onClick={() => openPopus(index)}
                                    />
                                  </div>
                                </div>
                              ))}
                            </div>

                            {/* Modal for full-screen image view */}
                            <Modal
                              show={isOpen}
                              onHide={toggleModal}
                              centered
                              size="lg"
                              dialogClassName="edge-to-edge-modal"
                              backdropClassName="no-backdrop-padding"
                            >
                              <Modal.Body className="p-2 position-relative">
                                <Button
                                  variant="light"
                                  onClick={toggleModal}
                                  style={{
                                    position: "absolute",
                                    top: "20px",
                                    right: "20px",
                                    zIndex: 2,
                                    borderRadius: "50%",
                                    padding: "0.2rem 0.6rem",
                                    boxShadow: "0 0 10px rgba(0,0,0,0.2)",
                                    fontSize: "20px",
                                    fontWeight: "bold",
                                  }}
                                >
                                  ✕
                                </Button>

                                {galleryImages.length > 0 && (
                                  <>
                                    <Button
                                      variant="light"
                                      onClick={prevImage}
                                      style={{
                                        position: "absolute",
                                        top: "45%",
                                        left: "20px",
                                        zIndex: 2,
                                        borderRadius: "50%",
                                        height: "2.5rem",
                                        width: "2.5rem",
                                        boxShadow:
                                          "0 0 10px rgba(0, 0, 0, 0.2)",
                                        fontSize: "30px",
                                        lineHeight: "30px",
                                        fontWeight: "bold",
                                        backgroundColor:
                                          "rgba(254, 254, 254, 0.32)",
                                      }}
                                    >
                                      ‹
                                    </Button>

                                    <Button
                                      variant="light"
                                      onClick={nextImage}
                                      style={{
                                        position: "absolute",
                                        top: "45%",
                                        right: "20px",
                                        zIndex: 2,
                                        borderRadius: "50%",
                                        height: "2.5rem",
                                        width: "2.5rem",
                                        boxShadow:
                                          "0 0 10px rgba(0, 0, 0, 0.2)",
                                        fontSize: "30px",
                                        lineHeight: "30px",
                                        fontWeight: "bold",
                                        backgroundColor:
                                          "rgba(254, 254, 254, 0.32)",
                                      }}
                                    >
                                      ›
                                    </Button>

                                    <img
                                      src={galleryImages[selectedIndex]}
                                      alt="Popup"
                                      style={{
                                        width: "100%",
                                        height: "auto",
                                        display: "block",
                                        borderRadius: "0",
                                        objectFit: "cover",
                                      }}
                                    />
                                  </>
                                )}
                              </Modal.Body>
                            </Modal>
                          </TabPanel>
                        )}

                        {/* Event Videos Tab using Native Bootstrap & Iframe */}
                        {resources.length > 0 && (
                          <TabPanel>
                            <div className="row mb-50">
                              {resources.map((resource, index) => (
                                <div className="col-lg-6 mb-30" key={index}>
                                  <div
                                    className="rounded shadow-sm overflow-hidden bg-white d-flex flex-column h-100"
                                    style={{ border: "1px solid #eaeaea" }}
                                  >
                                    <div className="p-3 border-bottom d-flex align-items-center">
                                      <i className="fa-brands fa-youtube text-danger fs-4 me-2"></i>
                                      <h6
                                        className="mb-0 fw-bold text-dark text-truncate"
                                        title={resource.label}
                                      >
                                        {resource.label}
                                      </h6>
                                    </div>
                                    {/* Responsive Iframe Container */}
                                    <div className="ratio ratio-16x9 flex-grow-1 bg-dark">
                                      <iframe
                                        className="position-absolute top-0 start-0 w-100 h-100 border-0"
                                        src={getYouTubeEmbedUrl(resource.link)}
                                        title={resource.label}
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                        referrerPolicy="strict-origin-when-cross-origin"
                                        allowFullScreen
                                      ></iframe>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </TabPanel>
                        )}
                      </div>
                    </div>
                  </Tabs>
                </article>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Related Events Section */}
      {relatedEvents && relatedEvents.length > 0 && (
        <div
          className="related-events__area pt-50 pb-50"
          style={{ backgroundColor: "#f8f9fa" }}
        >
          <div className="container">
            <div className="row">
              <div className="col-xxl-12">
                <div className="section__title-wrapper text-center mb-40">
                  <h3 className="section__title">
                    Related Events in {event?.category?.name}
                  </h3>
                  <p className="section__sub-title">
                    Discover more events you might be interested in
                  </p>
                </div>
              </div>
            </div>
            <div className="row">
              {relatedEvents.map((relatedEvent) => {
                // Extract date components for SingleEvent props
                const eventDate = new Date(relatedEvent.date);
                const day = eventDate.getDate().toString().padStart(2, "0");
                const month = eventDate.toLocaleString("en-US", {
                  month: "short",
                });
                const year = eventDate.getFullYear();

                return (
                  <div
                    key={relatedEvent._id}
                    className="col-xxl-4 col-xl-4 col-lg-4 col-md-6 mb-30"
                  >
                    <SingleEvent
                      eventId={relatedEvent._id}
                      eventImage={relatedEvent.image}
                      eventDate={day}
                      eventMonth={month}
                      eventYear={year}
                      title={relatedEvent.title}
                      description={
                        relatedEvent.description
                          ?.replace(/<[^>]*>/g, "") // Strip HTML tags
                          ?.split(" ")
                          .slice(0, 20)
                          .join(" ") +
                        (relatedEvent.description
                          ?.replace(/<[^>]*>/g, "")
                          ?.split(" ").length > 20
                          ? "..."
                          : "")
                      }
                      hasArrow={true}
                    />
                  </div>
                );
              })}
            </div>

            {/* View All Events Button */}
            <div className="row">
              <div className="col-xxl-12 text-center mt-30">
                <Link to="/event" className="ed-btn-theme">
                  View All Events
                  <i className="fa-solid fa-arrow-right ms-2"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default EventDetailsMain;
